import { getLanguageLabel, type Locale } from "@repo/fika-shared/src/locale"
import { useNavigate } from "@solidjs/router"
import { format } from "date-fns"
import { createEffect, createMemo, createSignal, Show } from "solid-js"
import { Box } from "@/designSystem/components/box"
import { Button } from "@/designSystem/components/button"
import { Form } from "@/designSystem/components/form"
import { HoldButton } from "@/designSystem/components/hold-button"
import { Select } from "@/designSystem/components/select"
import { Text } from "@/designSystem/components/text"
import { TextField } from "@/designSystem/components/text-field"
import { Toggle } from "@/designSystem/components/toggle"
import { Well } from "@/designSystem/components/well"
import { RightPanel } from "@/designSystem/patterns/right-panel"
import { Section } from "@/designSystem/patterns/section"
import { TagWithSuggestions } from "@/designSystem/patterns/tags-with-suggestions"
import { graphql } from "@/graphql"
import {
  createMutationWithErrorHandling,
  ErrorFragment,
} from "@/lib/graphql-mutation-helpers"
import { toast } from "@/lib/toast"
import { createBlogRouter } from "@/routes/posts-router"
import { useI18nContext } from "../../i18n/i18n-solid"
import { type EditorPost, usePostEditor } from "./post-editor-context"
import { PostEditorFragment } from "./post-editor-context/provider"

const UpdatePostSettingsMutation = graphql(
  `mutation UpdatePostSettings($id: ID!, $input: UpdatePostSettingsInput!) {
    updatePostSettings(id: $id, input: $input) {
      ... on MutationUpdatePostSettingsSuccess {
        data {
          id
          canonicalUrl
          pinnedAt
          mainLocale
          firstPublishedAt
        }
      }
      ...ErrorFragment
    }
  }`,
  [ErrorFragment],
)

const SavePostMutation = graphql(
  `mutation SavePostForSettings($id: ID!, $locale: Locale, $input: SavePostInput!) {
    savePost(id: $id, locale: $locale, input: $input) {
      ... on MutationSavePostSuccess {
        data {
          ...PostEditorFragment
        }
      }
      ...ErrorFragment
    }
  }`,
  [ErrorFragment, PostEditorFragment],
)

const ArchivePostMutation = graphql(
  `mutation ArchivePost($id: ID!, $archivedAt: DateTime) {
    archivePost(id: $id, archivedAt: $archivedAt) {
      ... on MutationArchivePostSuccess {
        data {
          id
          archivedAt
        }
      }
      ...ErrorFragment
    }
  }`,
  [ErrorFragment],
)

const DeletePostMutation = graphql(
  `mutation DeletePost($id: ID!) {
    deletePost(id: $id) {
      ... on MutationDeletePostSuccess {
        data
      }
      ...ErrorFragment
    }
  }`,
  [ErrorFragment],
)

export function PostSettings() {
  const postEditor = usePostEditor()
  const { LL } = useI18nContext()

  return (
    <Show
      when={!postEditor.isLoading() && postEditor.post()}
      fallback={<div>{LL().common.loading()}</div>}
    >
      {post => <SafePostSettings post={post()} />}
    </Show>
  )
}

function SafePostSettings(props: { post: EditorPost }) {
  const { LL } = useI18nContext()
  const navigate = useNavigate()
  const { paths } = createBlogRouter()

  const mainVersion = createMemo(() => {
    const mainLocale = props.post.mainLocale
    return props.post.currentVersions?.find(v => v.locale === mainLocale)
  })

  const enabledLocales = createMemo(() => {
    return (
      props.post.availableLocales?.filter(l => l !== props.post.mainLocale) ??
      []
    )
  })

  const hasTranslations = createMemo(() => enabledLocales().length > 0)
  const [selectedSeoLocale, setSelectedSeoLocale] = createSignal<Locale | null>(
    props.post.mainLocale as Locale,
  )

  const selectedVersion = createMemo(() => {
    const locale = selectedSeoLocale()
    if (!locale) return null

    return props.post.currentVersions?.find(v => v.locale === locale)
  })

  const availableSeoLocales = createMemo(() => {
    const userLocale = props.post.mainLocale as Locale
    const mainOption = {
      value: userLocale,
      label: `${getLanguageLabel(userLocale)} (Main)`,
      disabled: false,
    }

    if (!hasTranslations()) return [mainOption]

    const translationOptions = enabledLocales().map(locale => ({
      value: locale,
      label: `${getLanguageLabel(locale)}`,
      disabled: false,
    }))

    return [mainOption, ...translationOptions]
  })

  const defaultMetaTitle = createMemo(() => {
    const locale = selectedSeoLocale()
    if (!locale || locale === props.post.mainLocale) {
      const main = mainVersion()
      return main?.metaTitle || main?.title || ""
    }
    const version = selectedVersion()
    return version?.metaTitle || version?.title || ""
  })

  const defaultMetaDescription = createMemo(() => {
    const locale = selectedSeoLocale()
    if (!locale || locale === props.post.mainLocale) {
      const main = mainVersion()
      return main?.metaDescription || main?.description || ""
    }
    const version = selectedVersion()
    return version?.metaDescription || version?.description || ""
  })

  const [formPinned, setFormPinned] = createSignal(!!props.post.pinnedAt)
  const [formTitle, setFormTitle] = createSignal<string | undefined>(
    (defaultMetaTitle() as string) || undefined,
  )
  const [formDescription, setFormDescription] = createSignal<
    string | undefined
  >((defaultMetaDescription() as string | null) || undefined)

  createEffect(() => {
    setFormTitle((defaultMetaTitle() as string) || undefined)
    setFormDescription((defaultMetaDescription() as string | null) || undefined)
  })

  const [formCanonicalUrl, setFormCanonicalUrl] = createSignal(
    props.post.canonicalUrl || undefined,
  )

  function dateToLocalInput(date: Date | null | undefined): string {
    if (!date) return ""
    return format(new Date(date), "yyyy-MM-dd'T'HH:mm")
  }

  const initialPublishedAt = createMemo(() =>
    dateToLocalInput(props.post.firstPublishedAt),
  )

  const [formPublishedAt, setFormPublishedAt] = createSignal(
    initialPublishedAt(),
  )

  const publishedAtDirty = createMemo(
    () => formPublishedAt() !== initialPublishedAt(),
  )

  const seoMetadataDirty = createMemo(
    () =>
      formTitle() !== defaultMetaTitle() ||
      formDescription() !== defaultMetaDescription(),
  )

  const [updatePostSettingsState, updatePostSettings] =
    createMutationWithErrorHandling(
      UpdatePostSettingsMutation,
      "updatePostSettings",
    )
  const [savePostState, savePost] = createMutationWithErrorHandling(
    SavePostMutation,
    "savePost",
  )
  const [archivePostState, archivePost] = createMutationWithErrorHandling(
    ArchivePostMutation,
    "archivePost",
  )
  const [deletePostState, deletePost] = createMutationWithErrorHandling(
    DeletePostMutation,
    "deletePost",
  )

  const canonicalUrlDirty = createMemo(
    () => formCanonicalUrl() !== props.post.canonicalUrl,
  )

  const pinnedDirty = createMemo(() => formPinned() !== !!props.post.pinnedAt)
  const archived = createMemo(() => !!props.post.archivedAt)
  const alreadyPublishedOrArchived = createMemo(
    () => props.post.publishedAt || props.post.archivedAt,
  )
  const getTags = createMemo(() => new Set(props.post.tags?.tags ?? []))
  const suggestedTags = createMemo(() => {
    const tags = props.post.suggestedTags
    return tags ? tags.join(",") : null
  })

  async function onSubmitSeoMetadata() {
    const locale = selectedSeoLocale()

    if (!locale) return

    if (locale === props.post.mainLocale) {
      const version = props.post.currentVersions?.find(v => v.locale === locale)
      if (!version) return

      await savePost({
        id: props.post.id,
        locale,
        input: {
          title: version.title || "",
          description: version.description || "",
          content: version.content || "",
          imageHeaderUrl: version.imageHeaderUrl || "",
          metaTitle: formTitle() || null,
          metaDescription: formDescription() || null,
        },
      })
    } else {
      const version = selectedVersion()
      if (version) {
        await savePost({
          id: props.post.id,
          locale,
          input: {
            title: version.title || "",
            description: version.description || "",
            content: version.content || "",
            imageHeaderUrl: version.imageHeaderUrl || "",
            metaTitle: formTitle() || null,
            metaDescription: formDescription() || null,
          },
        })
      }
    }
    toast.success(LL().postSettings.seoMetadataSaved())
  }

  async function onSubmitCanonicalUrl() {
    await updatePostSettings({
      id: props.post.id,
      input: {
        canonicalUrl: formCanonicalUrl() || null,
      },
    })
    toast.success(LL().postSettings.canonicalUrlSaved())
  }

  async function onSubmitPublishedAt() {
    const value = formPublishedAt()
    await updatePostSettings({
      id: props.post.id,
      input: {
        firstPublishedAt: value ? new Date(value) : null,
      },
    })
    toast.success(LL().postSettings.publishedAtSaved())
  }

  async function onSubmitPin() {
    await updatePostSettings({
      id: props.post.id,
      input: {
        pinnedAt: props.post.pinnedAt ? null : new Date(),
      },
    })
    toast.success(LL().postSettings.pinSaved())
  }

  async function onArchive() {
    await archivePost({
      id: props.post.id,
      archivedAt: archived() ? null : new Date(),
    })
    toast.success(LL().postSettings.archivePostSaved())
  }

  async function onDelete() {
    await deletePost({
      id: props.post.id,
    })
    toast.success(LL().postSettings.deletePostSaved())
    navigate(paths().url)
  }

  return (
    <RightPanel
      closeHref={paths().post().url}
      header={{
        type: "title",
        title: LL().postSettings.title(),
      }}
    >
      <Form
        title={LL().postSettings.customPagesTitle()}
        description={LL().postSettings.customPagesDescription()}
        hue="amber"
        iconName="file-symlink"
        onSubmit={onSubmitPin}
      >
        <Toggle
          label={LL().postSettings.customPageLabel()}
          hue="amber"
          checked={formPinned()}
          onChange={setFormPinned}
        />
        <Form.Save
          hue="amber"
          disabled={!pinnedDirty()}
          active={updatePostSettingsState.fetching}
        />
      </Form>
      <Form
        title={LL().postSettings.seoMetadataTitle()}
        description={LL().postSettings.seoMetadataDescription()}
        hue="amber"
        iconName="captions"
        onSubmit={onSubmitSeoMetadata}
      >
        <Show when={hasTranslations()}>
          <Select
            hue="amber"
            label="Language"
            options={availableSeoLocales()}
            value={selectedSeoLocale}
            onChange={setSelectedSeoLocale}
          />
        </Show>
        <TextField value={formTitle()} onChange={setFormTitle}>
          <TextField.Label>
            {LL().postSettings.metaTitleLabel()}
          </TextField.Label>
          <TextField.TextArea
            rows="1"
            autoResize
            placeholder={LL().postSettings.metaTitlePlaceholder()}
          />
        </TextField>
        <TextField value={formDescription()} onChange={setFormDescription}>
          <TextField.Label>
            {LL().postSettings.metaDescriptionLabel()}
          </TextField.Label>
          <TextField.TextArea
            rows="1"
            autoResize
            placeholder={LL().postSettings.metaDescriptionPlaceholder()}
          />
        </TextField>
        <Form.Save
          hue="amber"
          disabled={!seoMetadataDirty()}
          active={savePostState.fetching}
        />
      </Form>
      <Form
        title={LL().postSettings.canonicalTitle()}
        description={LL().postSettings.canonicalDescription()}
        hue="amber"
        iconName="link"
        onSubmit={onSubmitCanonicalUrl}
      >
        <TextField value={formCanonicalUrl()} onChange={setFormCanonicalUrl}>
          <TextField.Label>
            {LL().postSettings.canonicalUrlLabel()}
          </TextField.Label>
          <TextField.Input
            type="text"
            placeholder="https://example.com/post/123"
          />
        </TextField>
        <Form.Save
          hue="amber"
          disabled={!canonicalUrlDirty()}
          active={updatePostSettingsState.fetching}
        />
      </Form>
      <Section
        title={LL().postSettings.tagsTitle()}
        description={LL().postSettings.tagsDescription()}
        hue="amber"
        iconName="tag"
      >
        <Well hue="sand">
          <TagWithSuggestions
            tags={getTags()}
            suggestions={suggestedTags()}
            targetType="post"
            targetId={props.post.id}
            hue="amber"
          />
        </Well>
      </Section>
      <Form
        title={LL().postSettings.publishedAtTitle()}
        description={LL().postSettings.publishedAtDescription()}
        hue="amber"
        iconName="calendar-days"
        onSubmit={onSubmitPublishedAt}
      >
        <TextField value={formPublishedAt()} onChange={setFormPublishedAt}>
          <TextField.Label>
            {LL().postSettings.publishedAtLabel()}
          </TextField.Label>
          <TextField.Input
            type="datetime-local"
            max={dateToLocalInput(new Date())}
          />
        </TextField>
        <Form.Save
          hue="amber"
          disabled={!publishedAtDirty()}
          active={updatePostSettingsState.fetching}
        />
      </Form>
      <Show when={alreadyPublishedOrArchived()}>
        <Section
          title={
            archived()
              ? LL().postSettings.unarchivePostTitle()
              : LL().postSettings.archivePostTitle()
          }
          description={
            archived()
              ? LL().postSettings.unarchivePostDescription()
              : LL().postSettings.archivePostDescription()
          }
          hue="orange"
          iconName="archive"
        >
          <Well hue="orange">
            <Box
              flexDirection="row"
              justifyContent="flex-end"
              alignItems="center"
              gap="12"
            >
              <Button
                hue={archived() ? "emerald" : "orange"}
                onClick={onArchive}
                active={archivePostState.fetching}
              >
                {archived()
                  ? LL().postSettings.unarchivePostTitle()
                  : LL().postSettings.archivePostTitle()}
              </Button>
            </Box>
          </Well>
        </Section>
      </Show>
      <Section
        title={LL().postSettings.deletePostTitle()}
        description={LL().postSettings.deletePostDescription()}
        hue="rose"
        iconName="trash-2"
      >
        <Well hue="rose">
          <Box
            flexDirection="column"
            justifyContent="space-between"
            alignItems="center"
            gap="12"
          >
            <Text color="rose800">{LL().postSettings.deletePostWarning()}</Text>
            <HoldButton
              hue="rose"
              icon="trash-2"
              label={LL().postSettings.holdToDelete()}
              labelProgress={LL().postSettings.deleting()}
              labelCompleted={LL().postSettings.deleted()}
              disabled={deletePostState.fetching}
              onHoldComplete={onDelete}
            />
          </Box>
        </Well>
      </Section>
    </RightPanel>
  )
}
